import { DecimalPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  ɵInternalFormsSharedModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
} from '@angular/material/dialog';
import { Button, InputComponent, HelperService } from '@shared';

export interface PartialRefundDialogData {
  /** Original chargeback amount as shown in the table (e.g. ₦10,000.00). */
  originalAmountDisplay: string;
  /** Upper bound for refund in major units (e.g. NGN). */
  maxRefundAmount: number;
  transactionRef?: string;
  customerEmail?: string;
}

export interface PartialRefundDialogResult {
  refundAmount: number;
  reason: string;
}

function coerceMaxMajorUnits(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(
      v
        .replace(/,/g, '')
        .replace(/[^\d.-]/g, '')
        .trim(),
    );
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Parse user-entered refund string to integer cents (2 decimal places). */
function parseAmountToCents(raw: string | null | undefined): number | null {
  if (raw == null) {
    return null;
  }
  const s = raw.trim();
  if (s === '') {
    return null;
  }
  const n = Number(s);
  if (!Number.isFinite(n)) {
    return null;
  }
  return Math.round(n * 100);
}

@Component({
  selector: 'app-partial-refund-dialog',
  standalone: true,
  imports: [
    Button,
    InputComponent,
    ɵInternalFormsSharedModule,
    ReactiveFormsModule,
    DecimalPipe,
  ],
  templateUrl: './partial-refund-dialog.html',
  styleUrl: './partial-refund-dialog.scss',
})
export class PartialRefundDialog {
  readonly data = inject<PartialRefundDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly helperService = inject(HelperService);
  private readonly dialog = inject(MatDialogRef<PartialRefundDialog>);

  /** Resolved cap in major units (handles callers passing numeric strings). */
  protected readonly resolvedMaxMajor = coerceMaxMajorUnits(
    this.data.maxRefundAmount,
  );

  private readonly maxRefundCents =
    this.resolvedMaxMajor == null
      ? null
      : Math.round(this.resolvedMaxMajor * 100);

  readonly form: FormGroup<{
    refundAmount: FormControl<string>;
    reason: FormControl<string>;
  }> = this.fb.group({
    refundAmount: this.fb.nonNullable.control('', {
      validators: [
        Validators.required,
        (c) => {
          const cents = parseAmountToCents(c.value);
          if (cents === null) {
            return c.value?.trim() === '' ? null : { invalidAmount: true };
          }
          if (cents < 1) {
            return { belowMin: true };
          }
          if (this.maxRefundCents === null) {
            return { exceedsMax: true };
          }
          if (cents > this.maxRefundCents) {
            return { exceedsMax: true };
          }
          return null;
        },
      ],
    }),
    reason: this.fb.nonNullable.control(''),
  });

  cancel(): void {
    this.dialog.close();
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.invalid) {
      this.helperService.validateAllFormFields(this.form);
      return;
    }

    const enteredCents = parseAmountToCents(this.refundAmount.value);
    if (
      enteredCents === null ||
      this.maxRefundCents === null ||
      enteredCents > this.maxRefundCents
    ) {
      this.refundAmount.setErrors({
        ...this.refundAmount.errors,
        exceedsMax: true,
      });
      this.refundAmount.markAsTouched();
      return;
    }

    const refundAmount = enteredCents / 100;
    const reason = this.form.controls.reason.value.trim();
    this.dialog.close({
      refundAmount,
      reason,
    } satisfies PartialRefundDialogResult);
  }

  get refundAmount(): FormControl<string> {
    return this.form.controls.refundAmount;
  }

  get reason(): FormControl<string> {
    return this.form.controls.reason;
  }
}
